/**
 * Stage A — one outcome model for every technician timer act.
 *
 * Each server authority keeps its own typed result (the wrappers in
 * ticketTimerManager / ticketWorkLifecycle); this module is the ONE place those
 * results are interpreted. It classifies each into
 *
 *   success          the server committed the act
 *   requires_action  someone else (or another act) is needed first —
 *                    pre-repair authorization, allocation review, resume
 *   refused          the server said no, for a stated reason
 *   failed           unknown outcome, transport or unexpected error
 *
 * and keeps the server's typed reason and detail. The server's answer is the
 * only input: nothing here predicts a refusal, and an outcome this module does
 * not recognise is a FAILURE — never inferred success.
 *
 * The user-facing words live here too, so every surface says the same thing.
 */
import { rolledForwardWords } from '@/utils/ticketTimerManager';
import type { TicketTimerProjection } from '@/utils/ticketWorkLifecycle';
import { CAPACITY_NOT_VERIFIED_TITLE, capacityNotVerifiedDescription } from '@/lib/location/liftCapacityNotice';
import { placementRefusalReason } from '@/lib/optiplace/placementRefusal';
import type {
  StartWorkResult,
  PauseWorkResult,
  ResumeWorkResult,
  DiagnosticCapturePauseResult,
  DiagnosticCaptureStopResult,
} from '@/utils/ticketTimerManager';
import type { StopWorkResult, EndPausedWorkResult, FinishWorkResult, DispositionJob } from '@/utils/ticketWorkLifecycle';
import type { TimerActionIntent } from './workState';

export interface TimerPresentation {
  tone: 'success' | 'info' | 'warning' | 'error';
  title: string;
  /** Present (possibly empty) only where the message carries a second line. */
  description?: string;
}

interface ResultBase {
  intent: Exclude<TimerActionIntent, 'REVIEW_TIME'>;
  /** The server's own outcome code (or FAILED). */
  outcome: string;
  /** What to tell the technician; null when the surface owns the next step (pre-repair authorization). */
  presentation: TimerPresentation | null;
  /** The server contradicted what was on screen: re-read this technician's state. */
  refreshMyState: boolean;
}

export interface TimerActionSuccess extends ResultBase {
  kind: 'success';
  outcome: 'STARTED' | 'PAUSED' | 'RESUMED' | 'STOPPED_AWAITING_REVIEW' | 'ENDED_FOR_REVIEW';
  /** The ticket projection the server reported afterwards; null = unknown (never "stopped"). */
  ticketTimerStatus: TicketTimerProjection | null;
  /** STOP / END_WORK: the server says this act left no live BOH work on the ticket. */
  ticketBecameIdle: boolean;
  /** PAUSE: the server says this pause left no OTHER live BOH work. */
  pauseLeftNoOtherLiveWork: boolean;
  /** PAUSE (Stage D): the attention the server raised with this pause, or null. */
  pauseAttention: string | null;
  /** STOP / END_WORK (Stage E): the automatic FOH handoff's custody event, or null. */
  fohHandoffEventId: string | null;
  /** STOP / END_WORK (Stage E): the same act moved the vehicle out of its bay. */
  vehicleMoved: boolean;
  /** STOP / END_WORK: the frozen allocation proposal to review. */
  reviewEpisodeId: string | null;
  /**
   * STOP / END_WORK: the final Stop completed the ticket (operational
   * completion). The card leaves the Dayboard, so a review must be offered
   * somewhere that outlives it.
   */
  ticketCompleted: boolean;
}

export type TimerRequirement =
  | { requirement: 'PRE_REPAIR_AUTHORIZATION'; blocker: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED'; fohNotified: boolean }
  | { requirement: 'ALLOCATION_REVIEW'; pendingReviews: number }
  | { requirement: 'RESUME_INSTEAD' }
  /**
   * Oct 20 (D-4): RESUME of paused work whose vehicle is parked — the technician chooses a bay and the SAME act is
   * resubmitted with that target (pull_in_and_resume_my_timer). Nothing changed yet.
   */
  | { requirement: 'PULL_IN_TO_BAY'; parkingSpotId: string | null };

export type TimerActionRequiresAction = ResultBase & { kind: 'requires_action' } & TimerRequirement;

export interface TimerActionRefused extends ResultBase {
  kind: 'refused';
  /** The server's reason text, when it gave one. */
  detail: string | null;
  /** START: the chosen work target (bay) is no longer valid — forget it, never retry it. */
  invalidatesTarget: boolean;
  /**
   * START: the vehicle is still parked and only the chosen target was refused
   * (BAY_NOT_AVAILABLE) — ask for a target again, from fresh occupancy. The
   * technician chooses; the client never substitutes one.
   */
  retryTarget: boolean;
  /** STOP / END_WORK: jobs the final technician must give a disposition for before retrying. */
  jobsNeedingDisposition: DispositionJob[];
  /**
   * STOP (oct20, CORE-AJ :46): the answer is given IN the open dialog (a
   * diagnostic outcome) — keep it and its inputs; nothing changed.
   */
  answerInDialog: boolean;
  /** STOP / END_WORK (oct20a S1): diagnostic jobs whose SESSION conclusion the dialog must ask for. */
  sessionConclusionJobs: Array<{ jobId: string; name: string }>;
}

export interface TimerActionFailed extends ResultBase {
  kind: 'failed';
  outcome: 'FAILED';
  message: string;
  /**
   * server: the authority answered FAILED — a definitive answer.
   * exception: the request threw; it may or may not have committed, so a
   * surface keeps the technician's inputs to retry truthfully.
   */
  source: 'server' | 'exception';
}

export type TimerActionResult =
  | TimerActionSuccess
  | TimerActionRequiresAction
  | TimerActionRefused
  | TimerActionFailed;

// ── helpers ──────────────────────────────────────────────────────────────────
const success = (
  intent: TimerActionSuccess['intent'], outcome: TimerActionSuccess['outcome'],
  presentation: TimerPresentation, extra: Partial<Pick<TimerActionSuccess,
    'ticketTimerStatus' | 'ticketBecameIdle' | 'pauseLeftNoOtherLiveWork' | 'pauseAttention' | 'reviewEpisodeId'
    | 'fohHandoffEventId' | 'vehicleMoved' | 'ticketCompleted'>> = {},
): TimerActionSuccess => ({
  kind: 'success', intent, outcome, presentation, refreshMyState: true,
  ticketTimerStatus: extra.ticketTimerStatus ?? null,
  ticketBecameIdle: extra.ticketBecameIdle === true,
  pauseLeftNoOtherLiveWork: extra.pauseLeftNoOtherLiveWork === true,
  pauseAttention: extra.pauseAttention ?? null,
  fohHandoffEventId: extra.fohHandoffEventId ?? null,
  vehicleMoved: extra.vehicleMoved === true,
  reviewEpisodeId: extra.reviewEpisodeId ?? null,
  ticketCompleted: extra.ticketCompleted === true,
});
const refused = (
  intent: TimerActionRefused['intent'], outcome: string, presentation: TimerPresentation,
  opts: {
    detail?: string | null; refreshMyState?: boolean; invalidatesTarget?: boolean; retryTarget?: boolean;
    jobsNeedingDisposition?: DispositionJob[]; answerInDialog?: boolean;
    sessionConclusionJobs?: Array<{ jobId: string; name: string }>;
  } = {},
): TimerActionRefused => ({
  kind: 'refused', intent, outcome, presentation,
  refreshMyState: opts.refreshMyState ?? true,
  detail: opts.detail ?? null,
  invalidatesTarget: opts.invalidatesTarget ?? false,
  retryTarget: opts.retryTarget ?? false,
  jobsNeedingDisposition: opts.jobsNeedingDisposition ?? [],
  answerInDialog: opts.answerInDialog ?? false,
  sessionConclusionJobs: opts.sessionConclusionJobs ?? [],
});
const failed = (
  intent: TimerActionFailed['intent'], message: string, presentation: TimerPresentation, refreshMyState: boolean,
  source: TimerActionFailed['source'] = 'server',
): TimerActionFailed => ({ kind: 'failed', intent, outcome: 'FAILED', message, presentation, refreshMyState, source });
/**
 * Oct 20 (D-4): a server/database message is evidence for the log, not words for a technician. A message that
 * looks like a raw database error (constraint, violates, SQLSTATE, relation, function …) is replaced by plain
 * words; anything else (already human text from a wrapper) is kept.
 */
export function humanTimerFailure(message: string | null | undefined, fallback: string): string {
  const m = String(message ?? '').trim();
  if (!m) return fallback;
  // NOV3: the session backstop's refusal for a carried-forward day — say it in words.
  if (/SEGMENT_ROLLED_FORWARD/.test(m)) {
    return rolledForwardWords(/carried forward to (\d{4}-\d{2}-\d{2})/.exec(m)?.[1] ?? null);
  }
  if (/violates|constraint|sqlstate|duplicate key|relation |function |syntax|null value|permission denied|check_violation|unique_violation/i.test(m)) {
    return `Something went wrong on the server. ${fallback}`;
  }
  return m;
}
const unknownOutcome = (r: unknown): string => String((r as { outcome?: unknown } | null)?.outcome ?? 'no outcome');
const preauth = (
  intent: TimerActionRequiresAction['intent'], blocker: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED', fohNotified: boolean,
): TimerActionRequiresAction => ({
  kind: 'requires_action', intent, outcome: blocker, presentation: null, refreshMyState: true,
  requirement: 'PRE_REPAIR_AUTHORIZATION', blocker, fohNotified,
});
const minutesLine = (measured: number, attributable: number): string => {
  const unattributed = measured - attributable;
  return `${measured} min measured` + (unattributed > 0 ? ` · ${unattributed} min not yet attributed to a job` : '');
};

/**
 * What to tell the technician after their own work stopped. #3: the line no
 * longer promises a review — a review with nothing to decide is resolved by
 * the server, and an ambiguous one announces itself by opening Review Time.
 */
export function stoppedHeadline(status: TicketTimerProjection | null): string {
  if (status === 'running') return 'Your work stopped (other work still running)';
  if (status === 'paused') return 'Your work stopped (other work on this ticket is paused)';
  if (status === 'stopped') return 'Work stopped';
  return 'Your work stopped';
}
/** What to tell the technician after their PAUSED episode was ended. */
export function endedHeadline(status: TicketTimerProjection | null): string {
  if (status === 'running') return 'Paused work ended (other work still running)';
  if (status === 'paused') return 'Paused work ended (other work on this ticket is paused)';
  if (status === 'stopped') return 'Paused work ended';
  return 'Your paused work ended';
}
/** What to tell the technician when THEY had nothing running. `running`/`paused` are not "idle". */
export function noTimerHeadline(status: TicketTimerProjection | null): string {
  if (status === 'running') return 'You had no timer running — other work on this ticket is still running';
  if (status === 'paused') return 'You had no timer running — other work on this ticket is paused';
  if (status === 'stopped') return 'No active timer to stop';
  return 'You had no timer running on this ticket';
}

// ── START (ordinary and parked) ──────────────────────────────────────────────
/**
 * Item #13 — the location writer's refusal reasons, in a technician's words.
 * The server's own sentence stays in `detail` (diagnostics); an unrecognised
 * one is never shown raw.
 */
export function bayRefusalWords(detail: string | null | undefined): string {
  const d = (detail ?? '').toLowerCase();
  if (/already occupied/.test(d)) return 'Another vehicle is already in that bay';
  if (/locked out/.test(d)) return 'That bay is locked out';
  if (/gvwr|lift capacity/.test(d)) return "That bay's lift can't take this vehicle's weight";
  if (/parking-category/.test(d)) return 'That bay is for parking, not work';
  if (/inactive|not available/.test(d)) return "That bay isn't in use right now";
  return "That bay can't take the vehicle right now";
}

/** Shown when the outcome is unknown or the request failed: no codes, no raw errors. */
export const START_FAILED_WORDS = 'Nothing was started. Try again — if it keeps happening, tell a manager.';

/** A FAILED message OptiBay wrote for people, safe to show as-is. */
const HUMAN_START_FAILURES = new Set([
  'Could not confirm this ticket is authorized for work. Nothing was started.',
]);

export function normalizeStartResult(r: StartWorkResult): TimerActionResult {
  const I = 'START' as const;
  switch (r.outcome) {
    case 'STARTED':
      return success(I, 'STARTED', r.capacityNotVerified === true
        // OD-33: the parked Start pulled the vehicle onto an unrated lift — started, and never presented as safe.
        ? { tone: 'warning', title: `${r.selectedJobIds.length === 1 ? 'Timer started' : `Timer started on ${r.selectedJobIds.length} jobs`} — ${CAPACITY_NOT_VERIFIED_TITLE}`, description: capacityNotVerifiedDescription() }
        : {
          tone: 'success',
          title: r.selectedJobIds.length === 1 ? 'Timer started' : `Timer started on ${r.selectedJobIds.length} jobs`,
        }, { ticketTimerStatus: r.ticketTimerStatus });
    case 'ALREADY_HAS_ACTIVE_TIMER':
      return refused(I, r.outcome, { tone: 'warning', title: 'You already have a timer running', description: 'Stop or pause it before starting work here.' });
    case 'RESUME_REQUIRED':
      return {
        kind: 'requires_action', intent: I, outcome: r.outcome, refreshMyState: true, requirement: 'RESUME_INSTEAD',
        presentation: { tone: 'warning', title: 'You have paused work on this ticket', description: 'Resume it instead of starting a new timer.' },
      };
    case 'REVIEW_REQUIRED_BEFORE_NEW_TIMER':
      return {
        kind: 'requires_action', intent: I, outcome: r.outcome, refreshMyState: true,
        requirement: 'ALLOCATION_REVIEW', pendingReviews: r.pendingReviews,
        presentation: { tone: 'warning', title: 'Time review required first', description: `${r.pendingReviews} stopped work episode(s) still need allocation review.` },
      };
    case 'SCOPE_STALE':
      return refused(I, r.outcome, { tone: 'error', title: 'Those jobs changed while you were choosing', description: 'Pick again — the selection was not started.' });
    case 'INVALID_JOB_SCOPE':
      return refused(I, r.outcome, { tone: 'error', title: 'Select at least one job to work on' }, { detail: r.reason });
    case 'ESTIMATE_NOT_STARTABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'Cannot start a timer on an estimate', description: 'Schedule the ticket first via OptiBoard.' });
    case 'VEHICLE_NOT_PRESENT':
      return refused(I, r.outcome, { tone: 'error', title: 'Vehicle is not present', description: 'Mark the vehicle present before starting work.' });
    case 'TICKET_NOT_STARTABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'This ticket is closed' });
    case 'NOT_TECHNICIAN':
      return refused(I, r.outcome, { tone: 'error', title: 'Only a technician in this shop can start work here' });
    case 'CUSTODY_WITH_FRONT_OFFICE':
      // Core-100 R4 W3 (CORE-AK): the advisor's window is never measured as technician work.
      return refused(I, r.outcome, { tone: 'warning', title: 'The front office has this vehicle', description: 'Work can start once they hand it back.' });
    case 'TICKET_NOT_AVAILABLE':
      // One message for "no such ticket" and "not your shop": the server does not distinguish them.
      return refused(I, r.outcome, { tone: 'error', title: 'This ticket is not available' });
    case 'ESTIMATE_REQUIRED':
    case 'AUTHORIZATION_REQUIRED':
      // Nothing started and (for a parked Start) nothing moved.
      return preauth(I, r.outcome, r.fohNotified);
    // STAGE 0D — the parked Start's own refusals. Nothing moved, nothing started.
    case 'BAY_NOT_AVAILABLE':
      return refused(I, r.outcome, {
        tone: 'error', title: "That bay can't take this vehicle",
        description: `${bayRefusalWords(r.detail)}. Nothing moved — choose another bay.`,
      }, { detail: r.detail, invalidatesTarget: true, retryTarget: true });
    case 'BAY_REQUIRED':
      return refused(I, r.outcome, { tone: 'error', title: 'Choose a bay to bring the vehicle into' }, { detail: r.detail, invalidatesTarget: true });
    case 'ALREADY_IN_BAY':
      return refused(I, r.outcome, {
        tone: 'error', title: 'This vehicle is already in a bay',
        description: 'Nothing moved. Tap Start again to work where it is.',
      }, { detail: r.detail, invalidatesTarget: true });
    case 'NOT_PARKED':
      return refused(I, r.outcome, {
        tone: 'error', title: 'Record where it is first',
        description: "OptiBay hasn't recorded where this vehicle is parked. Record its location (or check it in) before starting work. Nothing moved.",
      }, { detail: r.detail, invalidatesTarget: true });
    case 'NOT_ENTITLED_TO_TICKET':
      return refused(I, r.outcome, {
        tone: 'error', title: 'This vehicle is not scheduled or assigned to you',
        description: 'Nothing was started. Ask a manager to assign it to you.',
      }, { detail: r.detail });
    case 'WORK_ROLE_REFUSED':
      // Server authority (ordinary and parked Start alike). The server's own
      // sentence names internal work families, so it is kept in `detail` for
      // diagnostics and never shown to the technician.
      return refused(I, r.outcome, {
        tone: 'error', title: "You're not eligible for this type of work.",
        description: 'Nothing was started. Ask a manager to assign it to someone who does this work.',
      }, { detail: r.detail });
    case 'FAILED':
      // The raw error stays in `message` for diagnostics; the technician reads
      // people's words (never "Unexpected start outcome: X", never a payload).
      return failed(I, r.message, {
        tone: 'error', title: 'Failed to start timer',
        description: HUMAN_START_FAILURES.has(r.message) ? r.message : START_FAILED_WORDS,
      }, true);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(I, `Unexpected start outcome: ${o}`, { tone: 'error', title: 'Failed to start timer', description: START_FAILED_WORDS }, true);
    }
  }
}

// ── PAUSE ────────────────────────────────────────────────────────────────────
export function normalizePauseResult(r: PauseWorkResult): TimerActionResult {
  const I = 'PAUSE' as const;
  switch (r.outcome) {
    case 'PAUSED':
      return success(I, 'PAUSED', {
        tone: 'success',
        // Technician-scoped: the ticket may still be running for someone else.
        title: r.ticketTimerStatus === 'running' ? 'Your timer paused — other work on this ticket is still running' : 'Ticket timer paused',
      }, { ticketTimerStatus: r.ticketTimerStatus, pauseLeftNoOtherLiveWork: r.pauseLeftNoOtherLiveWork, pauseAttention: r.pauseAttention });
    case 'NO_ACTIVE_TIMER':
      return refused(I, r.outcome, { tone: 'info', title: 'You have no running timer on this ticket' });
    case 'EPISODE_AWAITING_REVIEW':
      return refused(I, r.outcome, { tone: 'warning', title: 'That work has stopped and needs allocation review — it cannot be paused' });
    case 'TICKET_NOT_AVAILABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'This ticket is not available' });
    case 'NOT_TECHNICIAN':
      return refused(I, r.outcome, { tone: 'error', title: 'Only a technician in this shop can pause work here' });
    case 'FAILED':
      // A transport failure is not evidence the display is stale.
      return failed(I, r.message, { tone: 'error', title: 'Could not pause', description: r.message }, false);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(I, `Unexpected pause outcome: ${o}`, { tone: 'error', title: 'Could not pause', description: `Unexpected pause outcome: ${o}` }, false);
    }
  }
}

/** Refusals shared by both combined diagnostic-capture authorities. */
function captureRefusal(
  intent: 'PAUSE' | 'STOP',
  r: Exclude<DiagnosticCapturePauseResult, { outcome: 'PAUSED' }> | Exclude<DiagnosticCaptureStopResult, { outcome: 'STOPPED_AWAITING_REVIEW' }>,
): TimerActionResult {
  switch (r.outcome) {
    case 'CAPTURE_SCOPE_STALE':
      return refused(intent, r.outcome, {
        tone: 'error', title: 'Those diagnostic jobs changed while you were recording',
        description: 'Nothing was saved and your timer did not change — reopen and record again.',
      });
    case 'CAPTURE_ALREADY_RECORDED':
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'Those results were already recorded',
        description: 'An existing diagnostic observation was not overwritten.',
      });
    case 'INVALID_CAPTURE':
      return refused(intent, r.outcome, { tone: 'error', title: 'Diagnostic results are incomplete', description: r.reason }, { detail: r.reason });
    case 'NO_ACTIVE_TIMER':
      return refused(intent, r.outcome, { tone: 'info', title: 'You have no running timer on this ticket' });
    case 'TICKET_NOT_AVAILABLE':
      return refused(intent, r.outcome, { tone: 'error', title: 'This ticket is not available' });
    case 'NOT_TECHNICIAN':
      return refused(intent, r.outcome, { tone: 'error', title: 'Only a technician in this shop can record results here' });
    case 'EPISODE_AWAITING_REVIEW':
      return refused(intent, r.outcome, { tone: 'warning', title: 'That work has stopped and needs allocation review' });
    case 'FAILED':
      return failed(intent, r.message, { tone: 'error', title: 'Could not save diagnostic results', description: r.message }, true);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(intent, `Unexpected capture outcome: ${o}`, { tone: 'error', title: 'Could not save diagnostic results', description: `Unexpected capture outcome: ${o}` }, true);
    }
  }
}

export function normalizePauseWithCaptureResult(r: DiagnosticCapturePauseResult): TimerActionResult {
  if (r.outcome === 'PAUSED') {
    // No "time locked": nothing was locked, and no minutes were created by recording a finding.
    return success('PAUSE', 'PAUSED', {
      tone: 'success',
      title: r.ticketTimerStatus === 'running'
        ? `Your timer paused — diagnostic results saved for ${r.capturedJobCount} job(s); other work is still running`
        : `Timer paused — diagnostic results saved for ${r.capturedJobCount} job(s)`,
    }, { ticketTimerStatus: r.ticketTimerStatus, pauseLeftNoOtherLiveWork: r.pauseLeftNoOtherLiveWork, pauseAttention: r.pauseAttention });
  }
  return captureRefusal('PAUSE', r);
}

// ── RESUME ───────────────────────────────────────────────────────────────────
export function normalizeResumeResult(r: ResumeWorkResult): TimerActionResult {
  const I = 'RESUME' as const;
  switch (r.outcome) {
    case 'RESUMED':
      return success(I, 'RESUMED', {
        tone: 'success',
        title: r.droppedJobIds.length
          ? `Timer resumed — ${r.droppedJobIds.length} job(s) were no longer open and were dropped`
          : 'Ticket timer resumed',
      }, { ticketTimerStatus: r.ticketTimerStatus });
    // Each of these means the server disagreed with what was on screen.
    case 'NO_PAUSED_WORK':
      return refused(I, r.outcome, { tone: 'info', title: 'You have no paused work to resume on this ticket' });
    case 'ALREADY_HAS_ACTIVE_TIMER':
      return refused(I, r.outcome, { tone: 'warning', title: 'You already have another timer running' });
    case 'SCOPE_REVIEW_REQUIRED':
      return refused(I, r.outcome, { tone: 'warning', title: 'The jobs you were working on are no longer open — the work scope needs review before resuming' });
    case 'REVIEW_REQUIRED_BEFORE_NEW_TIMER':
      return {
        kind: 'requires_action', intent: I, outcome: r.outcome, refreshMyState: true,
        requirement: 'ALLOCATION_REVIEW', pendingReviews: r.pendingReviews,
        presentation: {
          tone: 'warning',
          title: r.pendingReviews === 1
            ? 'Your stopped work episode still needs allocation review'
            : `${r.pendingReviews} stopped work episodes still need allocation review`,
        },
      };
    case 'EPISODE_AWAITING_REVIEW':
      return refused(I, r.outcome, { tone: 'warning', title: 'That work was sent for allocation review — it cannot be resumed' });
    case 'TICKET_NOT_AVAILABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'This ticket is not available' });
    case 'NOT_TECHNICIAN':
      return refused(I, r.outcome, { tone: 'error', title: 'Only a technician in this shop can resume work here' });
    case 'CUSTODY_WITH_FRONT_OFFICE':
      return refused(I, r.outcome, { tone: 'warning', title: 'The front office has this vehicle', description: 'Your pause is kept; resume once they hand it back.' });
    case 'ESTIMATE_REQUIRED':
    case 'AUTHORIZATION_REQUIRED':
      // The pause is untouched; nothing resumed.
      return preauth(I, r.outcome, r.fohNotified);
    // Oct 20 (D-4): the vehicle is parked — guide, never surface a constraint.
    case 'PARKED_PULL_IN_REQUIRED':
      return {
        kind: 'requires_action', intent: I, outcome: r.outcome, refreshMyState: false,
        requirement: 'PULL_IN_TO_BAY', parkingSpotId: r.parkingSpotId,
        presentation: { tone: 'info', title: 'This vehicle is parked', description: 'Choose the bay to bring it into, then your work resumes.' },
      };
    case 'BAY_NOT_AVAILABLE':
      return refused(I, r.outcome, {
        tone: 'warning', title: 'That bay cannot take the vehicle',
        description: `${r.detail ?? 'Choose another bay.'} Nothing moved and your work is still paused.`,
      }, { detail: r.detail, retryTarget: true, refreshMyState: false });
    case 'NOT_PARKED':
      return refused(I, r.outcome, { tone: 'info', title: 'The vehicle is no longer parked', description: 'Press Resume again.' });
    case 'WORK_ROLE_REFUSED':
      return refused(I, r.outcome, { tone: 'error', title: 'You cannot resume this work', description: r.detail ?? 'Your work roles do not cover it.' }, { detail: r.detail });
    case 'FAILED':
      // OCT28 #13: resuming a released repair is a fresh placement. Its refusals are the placement guard's and the
      // waiting-on-dependency gate's — said in words, still hard (nothing moved, the pause is kept).
      if (/INTERRUPTED_REPAIR_AWAITING_DEPENDENCY/.test(r.message ?? '')) {
        return failed(I, r.message, { tone: 'warning', title: 'This repair is waiting on an outside dependency',
          description: 'The front office marks it ready when the dependency is resolved. Your work is still paused and the vehicle has not moved.' }, true);
      }
      {
        const guard = placementRefusalReason({ message: r.message });
        if (guard) {
          return failed(I, r.message, { tone: 'warning', title: 'This repair cannot be placed back with you right now',
            description: `${guard === 'TECHNICIAN_INTERVAL_CONFLICT' ? 'Other work now occupies that time.' : guard === 'BAY_NOT_AVAILABLE'
              ? 'That bay cannot take it at that time.' : 'The schedule does not allow it at that time.'} Your work is still paused and the vehicle has not moved — ask the front office to place it.` }, true);
        }
      }
      // Oct 20 (D-4): never put a raw database message in front of the technician (it stays in `message`).
      return failed(I, r.message, { tone: 'error', title: 'Could not resume', description: humanTimerFailure(r.message, 'Your work is still paused.') }, false);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(I, `Unexpected resume outcome: ${o}`, { tone: 'error', title: 'Could not resume', description: `Unexpected resume outcome: ${o}` }, false);
    }
  }
}

// ── STOP ─────────────────────────────────────────────────────────────────────
export function normalizeStopResult(r: StopWorkResult): TimerActionResult {
  const I = 'STOP' as const;
  switch (r.outcome) {
    case 'STOPPED_AWAITING_REVIEW':
      return success(I, 'STOPPED_AWAITING_REVIEW', {
        tone: 'success', title: stoppedHeadline(r.ticketTimerStatus),
        description: minutesLine(r.measuredMinutes, r.attributableMinutes),
      }, { ticketTimerStatus: r.ticketTimerStatus, ticketBecameIdle: r.ticketBecameIdle, reviewEpisodeId: r.workEpisodeId });
    case 'NO_ACTIVE_TIMER':
      // YOU had nothing running — a different fact from the ticket being idle.
      return refused(I, r.outcome, { tone: 'info', title: noTimerHeadline(r.ticketTimerStatus) });
    case 'TICKET_NOT_AVAILABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'Could not stop work', description: 'This ticket is not available.' });
    case 'NOT_TECHNICIAN':
      return refused(I, r.outcome, { tone: 'error', title: 'Could not stop work', description: 'Only a technician in this shop can stop timed work here.' });
    case 'FAILED':
      return failed(I, r.message, { tone: 'error', title: 'Could not stop work', description: r.message }, true);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(I, `Unexpected stop outcome: ${o}`, { tone: 'error', title: 'Could not stop work', description: `Unexpected stop outcome: ${o}` }, true);
    }
  }
}

export function normalizeStopWithCaptureResult(r: DiagnosticCaptureStopResult): TimerActionResult {
  if (r.outcome === 'STOPPED_AWAITING_REVIEW') {
    // SAVE FINDINGS + STOP FOR REVIEW (this RPC alone completes nothing; the
    // technician's Stop is finish_my_work, which completes the ticket for the
    // final live worker — see normalizeFinishResult).
    return success('STOP', 'STOPPED_AWAITING_REVIEW', {
      tone: 'success',
      title: r.ticketWorkStillRunning
        ? 'Work stopped — diagnostic results saved (other work still running)'
        : 'Work stopped — diagnostic results saved',
      description: `${r.measuredMinutes} min measured · time allocation needs review`
        + (r.measuredMinutes - r.attributableMinutes > 0
          ? ` · ${r.measuredMinutes - r.attributableMinutes} min not yet attributed to a job` : ''),
    }, { ticketTimerStatus: r.ticketTimerStatus, ticketBecameIdle: r.ticketBecameIdle, reviewEpisodeId: r.workEpisodeId });
  }
  return captureRefusal('STOP', r);
}

// ── END WORK (a paused technician ends their episode) ────────────────────────
export function normalizeEndWorkResult(r: EndPausedWorkResult): TimerActionResult {
  const I = 'END_WORK' as const;
  switch (r.outcome) {
    case 'ENDED_FOR_REVIEW':
      return success(I, 'ENDED_FOR_REVIEW', {
        tone: 'success', title: endedHeadline(r.ticketTimerStatus),
        description: minutesLine(r.measuredMinutes, r.attributableMinutes),
      }, { ticketTimerStatus: r.ticketTimerStatus, ticketBecameIdle: r.ticketBecameIdle, reviewEpisodeId: r.workEpisodeId });
    case 'NO_PAUSED_WORK':
      return refused(I, r.outcome, { tone: 'info', title: 'You have no paused work on this ticket' });
    case 'TICKET_NOT_AVAILABLE':
      return refused(I, r.outcome, { tone: 'error', title: 'Could not end paused work', description: 'This ticket is not available.' });
    case 'NOT_TECHNICIAN':
      return refused(I, r.outcome, { tone: 'error', title: 'Could not end paused work', description: 'Only a technician in this shop can end timed work here.' });
    case 'FAILED':
      return failed(I, r.message, { tone: 'error', title: 'Could not end paused work', description: r.message }, true);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(I, `Unexpected end-work outcome: ${o}`, { tone: 'error', title: 'Could not end paused work', description: `Unexpected end-work outcome: ${o}` }, true);
    }
  }
}

// ── STAGE E: STOP / END WORK through finish_my_work ─────────────────────────
/**
 * One normaliser for both ends of work. The success wording says what the
 * SERVER did: only this technician's work ended, or — as the last live BOH
 * work — the ticket was COMPLETED, the vehicle left somewhere true on site and
 * the ticket sent to the front office. Refusals changed nothing; a destination
 * refusal re-asks the location (never a substitute). Unknown = FAILED.
 */
export function normalizeFinishResult(intent: 'STOP' | 'END_WORK', r: FinishWorkResult): TimerActionResult {
  const verb = intent === 'END_WORK' ? 'end paused work' : 'stop work';
  const cannot = `Could not ${verb}`;
  const nothingChanged = intent === 'END_WORK'
    ? 'Nothing changed — your work is still paused.'
    : 'Nothing changed — your timer is still running.';
  switch (r.outcome) {
    case 'FINISHED': {
      // oct20 FD-02: a final Stop that left work unfinished / could not complete
      // (any withheld reason other than the Detail Specialist's own).
      const reasons = r.withheldReasons ?? [];
      const unfinishedHandoff = r.completionWithheld && reasons.some((x) => !/Detail Specialist/.test(x));
      const title = r.entireRepair && r.ticketCompleted
        // OCT28 #14: the whole multi-day repair finished early.
        ? 'Entire repair complete — later scheduled days released, sent to the front office'
        : r.rolloverSegmentContinues
        // oct20 D-2: a day with a live next day — never "ticket completed".
        ? 'Today\'s work stopped — this multi-day repair continues on its next day'
        : r.ticketCompleted
        ? (r.vehicleMoved ? 'Work complete — ticket completed, vehicle parked, sent to the front office'
                          : 'Work complete — ticket completed and sent to the front office')
        : unfinishedHandoff ? (r.vehicleMoved ? 'Work stopped — ticket NOT completed, vehicle parked, sent to the front office'
                                              : 'Work stopped — ticket NOT completed, sent to the front office')
        : r.completionWithheld ? 'Your detail work is finished — the front office will complete this visit'
        : r.fohHandoffEventId ? 'Work finished — ticket sent to the front office'
        : r.ended === 'ENDED_FOR_REVIEW' ? endedHeadline(r.ticketTimerStatus) : stoppedHeadline(r.ticketTimerStatus);
      const findings = r.capturedJobCount > 0 ? ` · diagnostic results saved for ${r.capturedJobCount} job(s)` : '';
      const why = unfinishedHandoff ? ` · ${reasons.join('; ')}` : '';
      // OCT28 #14-c: an inspection left open or not performed is said, never hidden.
      const openInspections = (r.inspections ?? []).filter((x) => x.outcome !== 'COMPLETED')
        .map((x) => `${x.name}: ${x.outcome === 'INCOMPLETE' ? 'incomplete' : 'not performed'}`);
      const inspectionLine = openInspections.length > 0 ? ` · ${openInspections.join('; ')}` : '';
      return success(intent, r.ended, {
        tone: 'success', title,
        description: `${minutesLine(r.measuredMinutes, r.attributableMinutes)}${findings}${why}${inspectionLine}`,
      }, {
        ticketTimerStatus: r.ticketTimerStatus, ticketBecameIdle: r.ticketBecameIdle,
        reviewEpisodeId: r.workEpisodeId, fohHandoffEventId: r.fohHandoffEventId, vehicleMoved: r.vehicleMoved,
        ticketCompleted: r.ticketCompleted,
      });
    }
    case 'JOB_DISPOSITION_REQUIRED':
      return refused(intent, r.outcome, {
        tone: 'warning',
        title: r.jobs.some((j) => j.kind === 'inspection') ? 'Say what happened to the inspection' : 'Some jobs have no recorded work',
        description: `Say what happened to ${r.jobs.map((j) => j.name).join(', ')} to complete the ticket, or return to work. ${nothingChanged}`,
      }, { jobsNeedingDisposition: r.jobs });
    case 'INVALID_JOB_DISPOSITION':
      return refused(intent, r.outcome, r.reason === 'INSPECTION_WAS_WORKED'
        ? { tone: 'warning', title: 'This inspection was worked on',
            description: `${(r.jobs ?? []).map((j) => j.name).join(', ') || 'The inspection'} has recorded work, so it cannot be "Not performed". `
              + `Choose Completed or Not finished. ${nothingChanged}` }
        : { tone: 'error', title: cannot, description: `A job answer was not complete. ${nothingChanged}` },
        r.reason === 'INSPECTION_WAS_WORKED' ? { jobsNeedingDisposition: (r.jobs ?? []).map((j) => ({ ...j, kind: 'inspection' as const, worked: true })) } : undefined);
    case 'ENTIRE_REPAIR_NOT_COMPLETED':
      // OCT28 #14: all or nothing — the server rolled back, so nothing is shown as done.
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'The entire repair could not be completed',
        description: `${r.reasons.length > 0 ? `${r.reasons.join('; ')}. ` : ''}Nothing changed — your timer is still running and the later scheduled days are still booked. `
          + "Finish the open work, or choose Today's Work Complete.",
      }, { detail: r.reasons.join('; ') || null, answerInDialog: true });
    case 'SUCCESSOR_STARTED':
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'A later day of this repair has already been worked',
        description: `The repair cannot be completed early from this day. Choose Today's Work Complete. ${nothingChanged}`,
      }, { answerInDialog: true });
    case 'ALREADY_COMPLETED':
      return refused(intent, r.outcome, { tone: 'info', title: 'This repair is already completed', description: 'Nothing else changed.' });
    case 'DIAGNOSTIC_OUTCOME_REQUIRED':
      // oct20 — CORE-AJ :46: the diagnosis cannot end without its outcome.
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'Record the diagnostic outcome first',
        description: `${r.jobs.map((j) => j.name).join(', ') || 'A diagnostic job'} in your work has no outcome, and a diagnosis cannot end without one. `
          + (r.resumeRequired
            ? 'Resume, then stop to record your findings (you can record "Partial — narrowed it down").'
            : 'Record what you found so far (you can choose "Partial — narrowed it down"), or Pause to continue later.')
          + ` ${nothingChanged}`,
      }, { answerInDialog: true });
    case 'SESSION_CONCLUSION_REQUIRED':
      // oct20a S1 — ending my diagnostic SESSION (not the diagnosis) needs what my session found.
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'Say what your session found',
        description: `${r.jobs.map((j) => j.name).join(', ') || 'A diagnostic job'} stays open for whoever finishes it. `
          + 'Choose what your session found and how sure you are (for example "Partial — narrowed it down"). '
          + `It is not the final diagnosis. ${nothingChanged}`,
      }, { answerInDialog: true, sessionConclusionJobs: r.jobs });
    case 'INVALID_SESSION_CONCLUSION':
      return refused(intent, r.outcome, {
        tone: 'error', title: cannot, description: `What your session found could not be saved. ${nothingChanged}`,
      }, { answerInDialog: true });
    case 'COMPLETION_NEEDS_ON_SITE':
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'A completed ticket stays on site',
        description: `Choose where on site you are leaving the vehicle. If it is going off site, pause instead. ${nothingChanged}`,
      }, { retryTarget: true });
    case 'COMPLETION_BLOCKED':
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'This ticket cannot be completed yet',
        description: `${completionBlockLine(intent, r.obligation, r.reasons)} ${nothingChanged}`,
      }, { detail: r.reasons.join('; ') || null });
    case 'DESTINATION_REQUIRED':
      return refused(intent, r.outcome, {
        tone: 'warning', title: 'Where are you leaving the vehicle?',
        description: 'You are the last technician on this ticket, so the bay must be released. Nothing has changed yet.',
      }, { retryTarget: true });
    case 'DESTINATION_NOT_AVAILABLE':
      return refused(intent, r.outcome, {
        tone: 'error', title: 'That location cannot take the vehicle',
        description: `${r.detail} Nothing moved and your work has not stopped — choose again.`,
      }, { detail: r.detail, retryTarget: true, invalidatesTarget: true });
    case 'INVALID_DESTINATION':
      return refused(intent, r.outcome, { tone: 'error', title: cannot, description: 'That location is not complete. Nothing changed.' },
        { retryTarget: true });
    case 'NO_WORK_TO_FINISH':
      // YOU had nothing to end — a different fact from the ticket being idle.
      return refused(intent, r.outcome, { tone: 'info', title: noTimerHeadline(r.ticketTimerStatus) });
    case 'NO_ACTIVE_TIMER':
      return refused(intent, r.outcome, { tone: 'info', title: noTimerHeadline(null) });
    case 'NO_PAUSED_WORK':
      return refused(intent, r.outcome, { tone: 'info', title: 'You have no paused work on this ticket' });
    case 'CAPTURE_SCOPE_STALE':
    case 'CAPTURE_ALREADY_RECORDED':
    case 'INVALID_CAPTURE':
      return captureRefusal('STOP', r);
    case 'TICKET_NOT_AVAILABLE':
      return refused(intent, r.outcome, { tone: 'error', title: cannot, description: 'This ticket is not available.' });
    case 'NOT_TECHNICIAN':
      return refused(intent, r.outcome, { tone: 'error', title: cannot, description: 'Only a technician in this shop can end timed work here.' });
    case 'FAILED':
      return failed(intent, r.message, { tone: 'error', title: cannot, description: r.message }, true);
    default: {
      const never: never = r;
      const o = unknownOutcome(never);
      return failed(intent, `Unexpected finish outcome: ${o}`, { tone: 'error', title: cannot, description: `Unexpected finish outcome: ${o}` }, true);
    }
  }
}

/** Why a final Stop could not complete the ticket, in the technician's words. */
function completionBlockLine(intent: 'STOP' | 'END_WORK', obligation: string, reasons: readonly string[]): string {
  switch (obligation) {
    case 'DIAGNOSTIC_REQUIRED':
      return intent === 'END_WORK'
        ? 'A diagnostic job has no recorded outcome. Resume, then stop to record your findings.'
        : 'Record the diagnostic findings for every diagnostic job to complete the ticket.';
    case 'COMEBACK_REQUIRED':
      return 'This is a comeback and needs a resolution from the service advisor first. Pause to hand it over.';
    default:
      return reasons.length > 0 ? `${reasons.join('; ')}.` : 'Something still keeps this ticket open.';
  }
}

/** A thrown error or rejected promise: always a failure, never success. */
export function failedFromException(intent: TimerActionFailed['intent'], error: unknown): TimerActionFailed {
  const titles: Record<TimerActionFailed['intent'], string> = {
    START: 'Failed to start timer',
    PAUSE: 'Pause failed',
    RESUME: 'Failed to resume timer',
    STOP: 'Failed to stop timer',
    END_WORK: 'Failed to stop timer',
  };
  const message = (error as { message?: string; details?: string } | null)?.message
    || (error as { details?: string } | null)?.details
    || (intent === 'PAUSE' ? 'Failed to pause timer' : 'Unexpected error');
  // Today's wording: Pause names the error in the title; the others say only that it failed.
  const presentation: TimerPresentation = intent === 'PAUSE'
    ? { tone: 'error', title: `Pause failed: ${message}` }
    : { tone: 'error', title: titles[intent] };
  return failed(intent, message, presentation, false, 'exception');
}
